import { useEffect, useState } from "react";
import { MessageCircle, X } from "lucide-react";
import { openSubscribe } from "./SubscribeModalHost";
import { useIsSubscribed } from "../lib/useIsSubscribed";
import { t as tRaw } from "../lib/i18n";
import { DigestLaunchNotice } from "./DigestLaunchNotice";

// Placement 2 — mobile-only sticky bottom bar. Appears after the user
// scrolls past ~3 article cards. Dismissable per session (localStorage).
// Hidden on ≥768px viewports via CSS breakpoints.
// Language sync note: this component is mounted at the App root (outside
// every per-page <LangContext.Provider>), so an inline `useT()` here would
// always return the createContext("en") default — same footgun that hit
// FloatingSubscribeCTA earlier. We source lang from `localStorage.ir_lang`
// and listen for the `ir_lang_changed` window event HomePage dispatches
// on every toggle so the pill stays in sync same-tab.
const DISMISS_KEY = "subscribe_mobile_bar_dismissed";
const SCROLL_TRIGGER_PX = 800;  // ≈ height of 3 article cards on mobile
const LANG_KEY = "ir_lang";
const readLang = () => {
  try {
    const v = localStorage.getItem(LANG_KEY);
    if (v === "en" || v === "es" || v === "nl" || v === "pap") return v;
  } catch (_) { /* ignore */ }
  return "en";
};

export const MobileStickyBar = () => {
  const isSubscribed = useIsSubscribed();
  const [lang, setLang] = useState(readLang);
  const [visible, setVisible] = useState(false);
  const [dismissed, setDismissed] = useState(true);
  const t = (k) => tRaw(lang, k);

  useEffect(() => {
    try {
      if (localStorage.getItem(DISMISS_KEY) === "1") return;
    } catch (_) { /* ignore */ }
    setDismissed(false);
    const onScroll = () => {
      if (window.scrollY >= SCROLL_TRIGGER_PX) {
        setVisible(true);
        window.removeEventListener("scroll", onScroll);
      }
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Re-read language whenever HomePage dispatches ir_lang_changed
  // (same-tab), and whenever the tab regains focus (cross-tab).
  useEffect(() => {
    const onLang = () => setLang(readLang());
    window.addEventListener("ir_lang_changed", onLang);
    window.addEventListener("storage", onLang);
    return () => {
      window.removeEventListener("ir_lang_changed", onLang);
      window.removeEventListener("storage", onLang);
    };
  }, []);

  if (dismissed || !visible || isSubscribed) return null;

  const dismiss = () => {
    try { localStorage.setItem(DISMISS_KEY, "1"); } catch (_) { /* ignore */ }
    setDismissed(true);
  };

  return (
    <div
      className="sm:hidden fixed left-0 right-0 bottom-0 z-[90] shadow-lg"
      data-testid="mobile-sticky-bar-wrap"
    >
      <DigestLaunchNotice
        variant="short"
        lang={lang}
        className="rounded-none border-x-0 border-b-0 text-[11px] py-1.5 px-3"
        testid="mobile-sticky-bar-launch-notice"
      />
      <div
        className="bg-[#1A2E35] text-white h-14 flex items-center justify-between px-3 gap-2"
        data-testid="mobile-sticky-bar"
      >
        <div className="flex items-center gap-2 min-w-0 flex-1">
          <MessageCircle size={20} className="text-[#25D366] shrink-0" />
          <span className="text-white text-[13px] font-bold truncate">
            {t("get_daily_digest")}
          </span>
          <button
            type="button"
            onClick={() => openSubscribe("mobile_bar")}
            className="bg-[#25D366] hover:bg-[#1fb954] text-white font-bold text-[13px] px-3 py-1.5 rounded-full transition-colors shrink-0"
            data-testid="mobile-sticky-bar-btn"
          >
            {t("digest_cta_button").replace(/\s*→\s*$/, "")}
          </button>
        </div>
        <button
          type="button"
          onClick={dismiss}
          aria-label="Dismiss"
          className="text-white/70 hover:text-white p-1 shrink-0"
          data-testid="mobile-sticky-bar-dismiss"
        >
          <X size={16} />
        </button>
      </div>
    </div>
  );
};
